import { useAvailablePromoCodesList } from '@if/core'
import { useErrorHandler } from '@if/mui'
import { EFinancingProgram } from '@if/core/types'
import { useMerchantFinancingConfig } from '@src/data/api/config/financing-config-api'
import { useGetApplicablePromoPlans } from '@src/data/api/merchant/merchant-api'
import { toCustomPromoPlanOption, toMerchantPaymentPlans } from '@src/data/mappers/promoPlanMappers'
import { useFeatureFlags, useFinancingConfig, useIsAppLoading } from '@src/data/selectors/app-config-selectors'
import { CustomPromoPlanOption } from '@src/data/types/PromoPlan'
import { CreditApplication } from '@src/data/types/CreditApplicationSchema'
import { FinancingConfigDto } from '@src/data/types/FinancingConfigDto'
import { Merchant } from '@src/data/types/Merchant'
import { MerchantPaymentPlan } from '@src/data/types/MerchantPaymentPlan'
import { useEffect, useMemo } from 'react'
import { BaseWorksheetFormConfig } from './BaseWorksheetForm.config'
import { buildBaseWorksheetFormConfig, resolveProgramConfig } from './BaseWorksheetForm.factories'

export interface MerchantPaymentPlanSource {
  isCustomPromoPlanMerchant: boolean
  customPromoPlan: CustomPromoPlanOption | null
  availablePaymentPlansOverride: MerchantPaymentPlan[] | null
  isPaymentPlanLoading: boolean
}

export const useMerchantPaymentPlanSource = (merchant: Merchant | null | undefined): MerchantPaymentPlanSource => {
  const { useCustomFlashPlan } = useFeatureFlags()
  const isInitializing = useIsAppLoading()
  const { report } = useErrorHandler()
  const isCustomPromoPlanMerchant = useCustomFlashPlan && !!merchant?.flashPlanDefinedInManageMerchant
  const [promoPlans, isFetchingPromoPlans, promoPlansError] = useGetApplicablePromoPlans(
    useCustomFlashPlan ? merchant?.id : undefined,
  )

  useEffect(() => {
    if (promoPlansError) report(promoPlansError)
  }, [promoPlansError, report])

  const { customPromoPlan, availablePaymentPlansOverride } = useMemo(() => {
    if (!useCustomFlashPlan) return { customPromoPlan: null, availablePaymentPlansOverride: null }
    if (isCustomPromoPlanMerchant)
      return { customPromoPlan: toCustomPromoPlanOption(promoPlans[0] ?? null), availablePaymentPlansOverride: null }
    return { customPromoPlan: null, availablePaymentPlansOverride: toMerchantPaymentPlans(promoPlans) }
  }, [useCustomFlashPlan, isCustomPromoPlanMerchant, promoPlans])

  return {
    isCustomPromoPlanMerchant,
    customPromoPlan,
    availablePaymentPlansOverride,
    isPaymentPlanLoading: isInitializing || (useCustomFlashPlan && isFetchingPromoPlans),
  }
}

export interface ResolvedFinancingConfig {
  financingConfig: FinancingConfigDto
  isLoading: boolean
}

export const useResolvedFinancingConfig = (
  financingProgramId: EFinancingProgram | undefined,
  merchantId: string | undefined,
): ResolvedFinancingConfig => {
  const programFinancingConfig = useFinancingConfig(financingProgramId)
  const [merchantFinancingConfig, isFetchingMerchantConfig] = useMerchantFinancingConfig(
    merchantId,
    financingProgramId,
  )
  const isLoading = !!merchantId && !!financingProgramId && !merchantFinancingConfig && isFetchingMerchantConfig
  return {
    financingConfig: merchantFinancingConfig ?? programFinancingConfig,
    isLoading,
  }
}

export interface UseBaseWorksheetFormConfigFromCreditAppInput {
  creditApplication: CreditApplication
  merchant: Merchant | undefined | null
}

export interface BaseWorksheetFormConfigResult {
  config: BaseWorksheetFormConfig
  isLoading: boolean
}

export const useBaseWorksheetFormConfigFromCreditApp = ({
  creditApplication,
  merchant,
}: UseBaseWorksheetFormConfigFromCreditAppInput): BaseWorksheetFormConfigResult => {
  const { financingConfig, isLoading } = useResolvedFinancingConfig(
    creditApplication.financingProgramId,
    merchant?.id,
  )
  const programConfig = resolveProgramConfig(creditApplication.financingProgramId)

  const decision = creditApplication.finalCreditDecision
  const interestRate = decision?.interestRate ?? programConfig.minInterestRate

  const availablePromoCodes = useAvailablePromoCodesList(
    merchant?.planPromoRules,
    creditApplication.normsCreditDecision,
    creditApplication.financingProgramId,
  )

  const { isCustomPromoPlanMerchant, customPromoPlan, availablePaymentPlansOverride, isPaymentPlanLoading } =
    useMerchantPaymentPlanSource(merchant)

  const config = useMemo(
    () =>
      buildBaseWorksheetFormConfig({
        financingProgramId: creditApplication.financingProgramId,
        creditApplicationId: creditApplication.id,
        merchantId: merchant?.id ?? '',
        financingConfig,
        stateIso: creditApplication.applicant.currentAddress.stateIso,
        hasCoApplicant: !!creditApplication.coApplicant,
        interestRate,
        maxPmtAmount: decision?.maxPmtAmount ?? 0,
        createdOn: creditApplication.createdOn,
        expiresOn: creditApplication.expiresOn,
        merchant: merchant ?? null,
        normsCreditDecision: creditApplication.normsCreditDecision,
        maxAmountFinanced: decision?.maxAmountFinanced ?? null,
        maxTermDuration: decision?.maxTermDuration ?? null,
        editLocked: creditApplication.editLocked,
        availablePromoCodes,
        isCustomPromoPlanMerchant,
        customPromoPlan,
        paymentPlansOverride: availablePaymentPlansOverride,
        isPaymentPlanLoading,
      }),
    [
      creditApplication,
      merchant,
      financingConfig,
      interestRate,
      decision,
      availablePromoCodes,
      isCustomPromoPlanMerchant,
      customPromoPlan,
      availablePaymentPlansOverride,
      isPaymentPlanLoading,
    ],
  )

  return { config, isLoading: isLoading || isPaymentPlanLoading }
}
